import React from 'react';
import { Terminal, XCircle, CheckCircle2, Cpu, FileText, Target, Search, ShieldAlert, Lock, Zap, Clock, AlertTriangle, Code } from 'lucide-react';
import { FadeIn } from './components/FadeIn';
import { CTAButton } from './components/CTAButton';
import { Footer } from './components/Footer';
export function App() {
  return <div className="min-h-screen bg-navy-900 text-slate-300 font-sans selection:bg-cyan-accent selection:text-navy-900 pb-24" data-id="element-0">
      {/* Top Warning Banner */}
      <div className="bg-red-500/10 border-b border-red-500/20 py-2 px-4 text-center" data-id="element-1">
        <p className="text-red-400 text-sm font-medium flex items-center justify-center gap-2" data-id="element-2">
          <AlertTriangle className="w-4 h-4" data-id="element-3" />
          El 75% de los currículums son descartados por robots antes de ser
          leídos por un humano.
        </p>
      </div>

      <main className="max-w-4xl mx-auto px-6 pt-16 sm:pt-24" data-id="element-4">
        {/* HERO SECTION */}
        <section className="text-center mb-24" data-id="element-5">
          <FadeIn data-id="element-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-accent/10 border border-cyan-accent/20 text-cyan-accent text-sm font-medium mb-8" data-id="element-7">
              <Terminal className="w-4 h-4" data-id="element-8" />
              <span data-id="element-9">Sistema de Inyección de Contexto IA</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-10">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-white leading-tight mb-6" data-id="element-11">
              El Fin del Rechazo Automático: <br className="hidden sm:block" data-id="element-12" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-accent to-blue-500" data-id="element-13">
                Cómo Obligar al Algoritmo a Elegir tu CV en 60 Segundos
              </span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-14">
            <p className="text-lg sm:text-xl text-slate-400 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-15">
              La guía definitiva de "Prompt Engineering" para profesionales
              latinos en USA que están cansados de ser ignorados por el software
              de reclutamiento (ATS).
            </p>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-16">
            <div className="relative mx-auto max-w-2xl mb-12 rounded-xl overflow-hidden border border-navy-800 shadow-2xl shadow-cyan-accent/5" data-id="element-17">
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 to-transparent z-10" data-id="element-18"></div>
              <img src="https://cdn.magicpatterns.com/uploads/2FE67CDjKhvEyUUBEuwSNX/mock_up_AI.png" alt="Consigue entrevistas en 7 días usando IA" className="w-full h-auto object-cover" data-id="element-19" />
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-20">
            <CTAButton href="#pricing" data-id="element-21">¡QUIERO EMPEZAR YA!</CTAButton>
          </FadeIn>
        </section>

        {/* OPENING HOOK */}
        <section className="mb-24 relative" data-id="element-22">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-accent/50 to-transparent rounded-full" data-id="element-23"></div>
          <FadeIn data-id="element-24">
            <div className="pl-6 sm:pl-8" data-id="element-25">
              <p className="text-xl text-white font-medium mb-6 leading-relaxed" data-id="element-26">
                Son las 11:00 PM. Acabas de enviar tu postulación para el
                trabajo que realmente quieres. Tienes la experiencia, los
                estudios y las ganas. Cuatro minutos después, recibes el mismo
                correo genérico de siempre:{' '}
                <span className="italic text-slate-400" data-id="element-27">
                  "Gracias por tu interés, pero hemos decidido avanzar con otros
                  candidatos".
                </span>
              </p>
              <p className="text-lg text-slate-400 leading-relaxed mb-6" data-id="element-28">
                No te rechazó un humano. Ni siquiera leyeron tu nombre. Te
                rechazó un algoritmo programado para descartar el 75% de los
                currículums antes de que lleguen a una oficina.
              </p>
              <p className="text-lg text-white font-semibold" data-id="element-29">
                Estás jugando un juego diseñado para que pierdas, a menos que
                conozcas el código de entrada.
              </p>
            </div>
          </FadeIn>
        </section>

        {/* THE PROBLEM */}
        <section className="mb-24 bg-navy-800 rounded-2xl p-8 sm:p-10 border border-navy-700 relative overflow-hidden" data-id="element-30">
          <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full blur-3xl" data-id="element-31"></div>
          <FadeIn data-id="element-32">
            <h2 className="text-3xl font-display font-bold text-white mb-6 flex items-center gap-3" data-id="element-33">
              <ShieldAlert className="text-red-500 w-8 h-8" data-id="element-34" />
              El Muro de Cristal Digital
            </h2>
            <p className="text-lg mb-6 leading-relaxed" data-id="element-35">
              El mercado laboral en Estados Unidos ha cambiado. Hoy, las
              empresas no buscan al "mejor" candidato; buscan al que mejor
              encaja en los parámetros de su software ATS (Applicant Tracking
              System).
            </p>
            <p className="text-lg leading-relaxed" data-id="element-36">
              Si tu CV tiene columnas, gráficos, fotos o palabras clave
              genéricas como "trabajador" o "entusiasta", el robot te marca como{' '}
              <span className="text-red-400 font-mono bg-red-500/10 px-2 py-1 rounded" data-id="element-37">
                "no legible"
              </span>{' '}
              y te envía directo a la papelera digital. Estás atrapado en un
              muro de cristal donde tu talento es invisible porque no hablas el
              idioma de las máquinas.
            </p>
          </FadeIn>
        </section>

        {/* WHAT DIDN'T WORK */}
        <section className="mb-24" data-id="element-38">
          <FadeIn data-id="element-39">
            <h2 className="text-3xl font-display font-bold text-white mb-10 text-center" data-id="element-40">
              Por qué lo que has intentado no funciona
            </h2>
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-41">
              {['Usar plantillas coloridas de Canva que los escáneres ATS no pueden leer.', 'Aplicar masivamente con el mismo PDF a 50 vacantes diferentes.', 'Confiar en redactores de CV tradicionales que no entienden de Inteligencia Artificial.', "Poner tus habilidades en gráficos de barras que el software interpreta como 'espacio vacío'."].map((item, i) => <div key={i} className="bg-navy-800/50 border border-red-500/20 rounded-xl p-6 flex gap-4 items-start" data-id="element-42">
                  <XCircle className="w-6 h-6 text-red-500 shrink-0 mt-1" data-id="element-43" />
                  <p className="text-slate-300" data-id="element-44">{item}</p>
                </div>)}
            </div>
          </FadeIn>
        </section>

        {/* THE UNIQUE MECHANISM */}
        <section className="mb-24 relative" data-id="element-45">
          <div className="absolute inset-0 bg-cyan-accent/5 blur-3xl rounded-full" data-id="element-46"></div>
          <FadeIn className="relative bg-navy-800 border border-cyan-accent/30 rounded-2xl p-8 sm:p-12 text-center" data-id="element-47">
            <Cpu className="w-16 h-16 text-cyan-accent mx-auto mb-6" data-id="element-48" />
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-white mb-6" data-id="element-49">
              Presentamos{' '}
              <span className="text-cyan-accent" data-id="element-50">The Ghost Resume Engine</span>
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-51">
              No es un curso de redacción, es un sistema de{' '}
              <strong className="text-white" data-id="element-52">"Inyección de Contexto"</strong>{' '}
              mediante Inteligencia Artificial.
            </p>
            <p className="text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto" data-id="element-53">
              Usamos modelos de lenguaje avanzados (Claude y ChatGPT) para
              realizar ingeniería inversa a las descripciones de empleo. El
              resultado es un "Ghost Resume": un documento que parece impecable
              para el ojo humano, pero que está saturado de los metadatos
              exactos que el algoritmo necesita para darte una puntuación de
              coincidencia del 95% o superior.
            </p>
          </FadeIn>
        </section>

        {/* WHAT YOU GET */}
        <section className="mb-24" data-id="element-54">
          <FadeIn data-id="element-55">
            <h2 className="text-3xl font-display font-bold text-white mb-10 text-center" data-id="element-56">
              Tu Arsenal de Infiltración
            </h2>
            <div className="space-y-4" data-id="element-57">
              {[{
              icon: Code,
              title: 'El Master Prompt de Reclutamiento',
              desc: 'El código exacto para que la IA reescriba tu experiencia en segundos.'
            }, {
              icon: FileText,
              title: "La Plantilla 'Single-Column Ghost'",
              desc: 'El diseño de texto plano que garantiza legibilidad total en cualquier ATS.'
            }, {
              icon: Target,
              title: 'Guía de Keyword Density',
              desc: 'Cómo identificar las 5 palabras clave que deciden tu sueldo.'
            }, {
              icon: Search,
              title: 'Auditoría de LinkedIn',
              desc: 'El método para sincronizar tu perfil digital con tu CV optimizado.'
            }, {
              icon: Lock,
              title: "Lista de 'Fuentes Prohibidas'",
              desc: 'Acceso a la lista de formatos y fuentes que confunden a los robots.'
            }].map((item, i) => <div key={i} className="bg-navy-800 border border-navy-700 rounded-xl p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center hover:border-cyan-accent/50 transition-colors" data-id="element-58">
                  <div className="bg-navy-900 p-3 rounded-lg border border-navy-700" data-id="element-59">
                    <item.icon className="w-6 h-6 text-cyan-accent" data-id="element-60" />
                  </div>
                  <div data-id="element-61">
                    <h3 className="text-white font-bold text-lg mb-1" data-id="element-62">
                      {item.title}
                    </h3>
                    <p className="text-slate-400" data-id="element-63">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </section>

        {/* WHO THIS IS FOR / NOT FOR */}
        <section className="mb-24 grid md:grid-cols-2 gap-8" data-id="element-64">
          <FadeIn data-id="element-65">
            <div className="bg-navy-800/50 border border-green-500/20 rounded-2xl p-8 h-full" data-id="element-66">
              <h3 className="text-2xl font-display font-bold text-white mb-6 flex items-center gap-2" data-id="element-67">
                <CheckCircle2 className="text-green-500" data-id="element-68" /> Para quién es
              </h3>
              <ul className="space-y-4" data-id="element-69">
                {['Profesionales en USA que no están recibiendo llamadas a pesar de estar calificados.', 'Latinos buscando dar el salto a corporaciones Fortune 500 donde los filtros son más estrictos.', 'Personas que quieren usar la Inteligencia Artificial para ahorrar horas de trabajo manual.'].map((text, i) => <li key={i} className="flex gap-3 text-slate-300" data-id="element-70">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2.5 shrink-0" data-id="element-71"></div>
                    <span data-id="element-72">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-73">
            <div className="bg-navy-800/50 border border-red-500/20 rounded-2xl p-8 h-full" data-id="element-74">
              <h3 className="text-2xl font-display font-bold text-white mb-6 flex items-center gap-2" data-id="element-75">
                <XCircle className="text-red-500" data-id="element-76" /> Para quién NO es
              </h3>
              <ul className="space-y-4" data-id="element-77">
                {['Personas que esperan que la IA mienta por ellos (no promovemos el fraude profesional).', 'Candidatos sin ninguna experiencia previa que buscan empleos de entrada nivel cero.', 'Quienes no estén dispuestos a dedicar 10 minutos a configurar sus prompts.'].map((text, i) => <li key={i} className="flex gap-3 text-slate-300" data-id="element-78">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2.5 shrink-0" data-id="element-79"></div>
                    <span data-id="element-80">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </section>

        {/* HOW IT WORKS */}
        <section className="mb-24" data-id="element-81">
          <FadeIn data-id="element-82">
            <h2 className="text-3xl font-display font-bold text-white mb-12 text-center" data-id="element-83">
              El Protocolo de 3 Pasos
            </h2>
            <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-84">
              <div className="hidden md:block absolute top-8 left-10 right-10 h-0.5 bg-navy-700 z-0" data-id="element-85"></div>

              {[{
              step: '01',
              title: 'Identifica',
              desc: 'Encuentra la descripción del empleo que deseas en LinkedIn o Indeed.'
            }, {
              step: '02',
              title: 'Inyecta',
              desc: 'Insertas el Master Prompt y tu CV actual en Claude.ai o ChatGPT.'
            }, {
              step: '03',
              title: 'Aplica',
              desc: 'Copias el resultado optimizado en nuestra plantilla Ghost y envías.'
            }].map((item, i) => <div key={i} className="relative z-10 flex flex-col items-center text-center" data-id="element-86">
                  <div className="w-16 h-16 rounded-full bg-navy-900 border-2 border-cyan-accent flex items-center justify-center text-xl font-display font-bold text-cyan-accent mb-6 shadow-[0_0_15px_rgba(0,212,255,0.2)]" data-id="element-87">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-88">
                    {item.title}
                  </h3>
                  <p className="text-slate-400" data-id="element-89">{item.desc}</p>
                </div>)}
            </div>
          </FadeIn>
        </section>

        {/* PROOF */}
        <section className="mb-24" data-id="element-90">
          <FadeIn data-id="element-91">
            <div className="bg-gradient-to-br from-navy-800 to-navy-900 border border-navy-700 rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden" data-id="element-92">
              <Zap className="absolute top-0 right-0 w-64 h-64 text-cyan-accent/5 -translate-y-1/2 translate-x-1/4" data-id="element-93" />
              <h2 className="text-3xl font-display font-bold text-white mb-6" data-id="element-94">
                Lo que cambia en 7 días
              </h2>
              <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed" data-id="element-95">
                Pasarás de tener una tasa de respuesta del 2% (promedio del
                mercado) a recibir notificaciones de{' '}
                <strong className="text-cyan-accent" data-id="element-96">
                  "Entrevista Programada"
                </strong>{' '}
                porque habrás hackeado la barrera de entrada que detiene a todos
                los demás.
              </p>
            </div>
          </FadeIn>
        </section>

        {/* FAQ */}
        <section className="mb-24 max-w-3xl mx-auto" data-id="element-97">
          <FadeIn data-id="element-98">
            <h2 className="text-3xl font-display font-bold text-white mb-10 text-center" data-id="element-99">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-6" data-id="element-100">
              {[{
              q: '¿Es seguro usar estos prompts?',
              a: 'Sí, es el estándar moderno de búsqueda de empleo. Las empresas usan IA para filtrar, tú usas IA para ser encontrado.'
            }, {
              q: '¿Necesito pagar por ChatGPT Plus?',
              a: 'No, el sistema funciona perfectamente con las versiones gratuitas de Claude o ChatGPT.'
            }, {
              q: '¿Funciona para cualquier industria?',
              a: 'Sí, los sistemas ATS son universales. Funciona desde construcción hasta ingeniería de software, finanzas o marketing.'
            }].map((faq, i) => <div key={i} className="bg-navy-800/50 border border-navy-700 rounded-xl p-6" data-id="element-101">
                  <h3 className="text-lg font-bold text-white mb-2 flex items-start gap-3" data-id="element-102">
                    <span className="text-cyan-accent font-display" data-id="element-103">Q.</span>
                    {faq.q}
                  </h3>
                  <p className="text-slate-400 pl-7" data-id="element-104">{faq.a}</p>
                </div>)}
            </div>
          </FadeIn>
        </section>

        {/* OFFER & PRICE BLOCK */}
        <section id="pricing" className="mb-12" data-id="element-105">
          <FadeIn data-id="element-106">
            <div className="bg-navy-800 border-2 border-cyan-accent/50 rounded-2xl p-8 sm:p-12 text-center relative shadow-[0_0_40px_rgba(0,212,255,0.1)]" data-id="element-107">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cyan-accent text-navy-900 font-bold px-4 py-1 rounded-full text-sm uppercase tracking-wider" data-id="element-108">
                Acceso Inmediato
              </div>

              <h2 className="text-3xl sm:text-4xl font-display font-bold text-white mb-6" data-id="element-109">
                Obtén The Ghost Resume Engine Hoy
              </h2>

              <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto" data-id="element-110">
                Deja de adivinar qué quieren los reclutadores y dáselo
                exactamente como lo pide su sistema.
              </p>

              <div className="mb-10" data-id="element-111">
                <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 text-green-400 font-bold text-sm uppercase tracking-wider px-4 py-1.5 rounded-full mb-4" data-id="element-112">
                  <Zap className="w-4 h-4" data-id="element-113" />
                  Oferta Especial — 76% de Descuento
                </div>
                <p className="text-slate-400 mb-3 uppercase tracking-wide text-sm font-semibold" data-id="element-114">
                  Inversión Única
                </p>
                <div className="flex justify-center items-center gap-4 mb-1" data-id="element-115">
                  <span className="text-2xl text-slate-500 line-through font-display" data-id="element-116">
                    $27.00
                  </span>
                </div>
                <div className="flex justify-center items-start gap-1" data-id="element-117">
                  <span className="text-2xl text-cyan-accent font-bold mt-2" data-id="element-118">
                    $
                  </span>
                  <span className="text-7xl font-display font-bold text-white" data-id="element-119">
                    6
                  </span>
                  <span className="text-4xl font-display font-bold text-white mt-2" data-id="element-120">
                    .39
                  </span>
                  <span className="text-xl text-slate-400 mt-8" data-id="element-121">USD</span>
                </div>
                <p className="text-sm text-slate-400 mt-4 max-w-md mx-auto" data-id="element-122">
                  Menos de lo que cuesta un café, para una herramienta que puede
                  añadir miles de dólares a tu salario anual.
                </p>
              </div>

              <CTAButton href="https://aigrowthsystem.it.com/cart/44200021262387:1" className="w-full sm:w-auto text-xl py-5 px-10" data-id="element-123">
                ¡QUIERO MI GHOST RESUME AHORA!
              </CTAButton>

              <p className="text-sm text-slate-500 mt-6 flex items-center justify-center gap-2" data-id="element-124">
                <Clock className="w-4 h-4" data-id="element-125" /> Acceso instantáneo tras la compra.
                Empieza a aplicar hoy mismo.
              </p>
            </div>
          </FadeIn>
        </section>

        <Footer data-id="element-126" />
      </main>
    </div>;
}